import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Settings,
  RefreshCw,
  Bot,
  Trophy,
  Users,
  Swords,
} from "lucide-react";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/hooks/use-auth";

// Form schema for game settings
const baseGameSettingsSchema = z.object({
  aiDifficulty: z.object({
    beginner: z.number(),
    pro: z.number(),
    default: z.number(),
  }),
  shuffleType: z.enum(["half", "full"]),
  deckType: z.string(),
  gbaaProtection: z.boolean(),
  gbaaPenalty: z.object({
    canCall: z.boolean(),
    true: z.number(),
    false: z.number(),
  }),
  winnerStart: z.boolean(),
  dryMode: z.boolean(),
  underTenEnabled: z.boolean(),
  gameScore: z.object({
    numberOfRounds: z.number(),
    pointsToWin: z.number(),
    direction: z.enum(["asc", "desc"]),
  }),
  searchTimeOut: z.number(),
  timer: z.object({
    timerEnabled: z.boolean(),
    timerDuration: z.number(),
    timerWarning: z.number(),
    timerWarningEnabled: z.boolean(),
  }),
  inactivityTimeoutTime: z.number(),
});

type GameSettings = z.infer<typeof baseGameSettingsSchema>;

const GAME_MODES = ["ranked", "quickPlay", "custom", "ai", "tournament"] as const;

export function GameSettingsTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeMode, setActiveMode] = useState<typeof GAME_MODES[number]>("quickPlay");

  // Fetch current settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ["/api/game-settings/current", activeMode],
    queryFn: () => apiRequest(`/api/game-settings/current/${activeMode}`),
  });

  const getDefaultValues = useCallback((mode: typeof GAME_MODES[number]): GameSettings => {
    const baseSettings: GameSettings = {
      aiDifficulty: { beginner: 1, pro: 7, default: 7 },
      shuffleType: "half" as const,
      deckType: "sixToKing",
      gbaaProtection: true,
      gbaaPenalty: { canCall: false, true: 3, false: 2 },
      winnerStart: false,
      dryMode: false,
      underTenEnabled: true,
      gameScore: {
        numberOfRounds: 10,
        pointsToWin: 10,
        direction: "asc",
      },
      searchTimeOut: 60000,
      timer: {
        timerEnabled: false,
        timerDuration: 20,
        timerWarning: 5,
        timerWarningEnabled: true,
      },
      inactivityTimeoutTime: 600000,
    };

    switch (mode) {
      case "ranked":
        return {
          ...baseSettings,
          gbaaProtection: false,
          gbaaPenalty: { ...baseSettings.gbaaPenalty, canCall: true },
          timer: { ...baseSettings.timer, timerEnabled: true },
        };
      case "quickPlay":
      case "ai":
        return baseSettings;
      case "custom":
      case "tournament":
        return {
          ...baseSettings,
          gbaaProtection: false,
          gbaaPenalty: { ...baseSettings.gbaaPenalty, canCall: true },
        };
      default:
        return baseSettings;
    }
  }, []);

  const form = useForm<GameSettings>({
    resolver: zodResolver(baseGameSettingsSchema),
    defaultValues: settings ?? getDefaultValues(activeMode),
  });

  // Update form when settings or mode changes
  useEffect(() => {
    if (settings) {
      form.reset(settings);
    } else {
      form.reset(getDefaultValues(activeMode));
    }
  }, [settings, activeMode, form, getDefaultValues]);

  const updateSettingsMutation = useMutation({
    mutationFn: async (data: GameSettings) => {
      if (!user?.id) {
        throw new Error("No user ID found");
      }

      // Transform nested data into flat structure
      const flattenedData = {
        aiDifficultyBeginner: data.aiDifficulty.beginner,
        aiDifficultyPro: data.aiDifficulty.pro,
        aiDifficultyDefault: data.aiDifficulty.default,
        shuffleType: data.shuffleType,
        deckType: data.deckType,
        gbaaProtection: data.gbaaProtection,
        gbaaPenaltyCanCall: data.gbaaPenalty.canCall,
        gbaaPenaltyTrue: data.gbaaPenalty.true,
        gbaaPenaltyFalse: data.gbaaPenalty.false,
        winnerStart: data.winnerStart,
        dryMode: data.dryMode,
        underTenEnabled: data.underTenEnabled,
        gameScoreNumberOfRounds: data.gameScore.numberOfRounds,
        gameScorePointsToWin: data.gameScore.pointsToWin,
        gameScoreDirection: data.gameScore.direction,
        searchTimeOut: data.searchTimeOut,
        timerEnabled: data.timer.timerEnabled,
        timerDuration: data.timer.timerDuration,
        timerWarning: data.timer.timerWarning,
        timerWarningEnabled: data.timer.timerWarningEnabled,
        inactivityTimeoutTime: data.inactivityTimeoutTime,
        updatedBy: user.id
      };

      return await apiRequest(`/api/game-settings/${activeMode}`, {
        method: "POST",
        body: JSON.stringify(flattenedData),
      });
    },
    onSuccess: () => {
      toast({
        title: "Settings Updated",
        description: "Game settings have been successfully updated.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/current/game-settings"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update settings",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: GameSettings) => {
    updateSettingsMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="animate-spin h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Settings className="h-6 w-6" />
          Game Settings
        </h2>
        <p className="text-muted-foreground">
          Configure settings for different game modes
        </p>
      </div>

      <Tabs value={activeMode} onValueChange={(v) => setActiveMode(v as typeof GAME_MODES[number])}>
        <TabsList>
          <TabsTrigger value="ranked" className="flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            Ranked
          </TabsTrigger>
          <TabsTrigger value="quickPlay" className="flex items-center gap-2">
            <Swords className="h-4 w-4" />
            Quick Play
          </TabsTrigger>
          <TabsTrigger value="custom" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Custom
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex items-center gap-2">
            <Bot className="h-4 w-4" />
            AI
          </TabsTrigger>
          <TabsTrigger value="tournament" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Tournament
          </TabsTrigger>
        </TabsList>

        {GAME_MODES.map((mode) => (
          <TabsContent key={mode} value={mode}>
            <Card>
              <CardHeader>
                <CardTitle>
                  {mode.charAt(0).toUpperCase() + mode.slice(1)} Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    {/* AI Difficulty Settings */}
                    <div className="space-y-4">
                      <h3 className="font-medium">AI Difficulty</h3>
                      <div className="grid grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name="aiDifficulty.beginner"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Beginner Level</FormLabel>
                              <FormControl>
                                <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="aiDifficulty.pro"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Pro Level</FormLabel>
                              <FormControl>
                                <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="aiDifficulty.default"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Default Level</FormLabel>
                              <FormControl>
                                <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    {/* Game Rules */}
                    <div className="space-y-4">
                      <h3 className="font-medium">Game Rules</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="shuffleType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Shuffle Type</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="half">Half</SelectItem>
                                  <SelectItem value="full">Full</SelectItem>
                                </SelectContent>
                              </Select>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="deckType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Deck Type</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="gbaaProtection"
                          render={({ field }) => (
                            <FormItem className="flex items-center justify-between rounded-lg border p-4">
                              <div>
                                <FormLabel>GBAA Protection</FormLabel>
                                <FormDescription>Protect player from wrong plays</FormDescription>
                              </div>
                              <FormControl>
                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <div className="space-y-4">
                          <h4 className="font-medium">GBAA Penalty</h4>
                          <FormField
                            control={form.control}
                            name="gbaaPenalty.canCall"
                            render={({ field }) => (
                              <FormItem className="flex items-center justify-between rounded-lg border p-4">
                                <div>
                                  <FormLabel>Can Call GBAA</FormLabel>
                                  <FormDescription>Allow opponents to call wrong plays</FormDescription>
                                </div>
                                <FormControl>
                                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <div className="grid grid-cols-2 gap-4">
                            <FormField
                              control={form.control}
                              name="gbaaPenalty.true"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Successful Call Penalty</FormLabel>
                                  <FormControl>
                                    <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="gbaaPenalty.false"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Failed Call Penalty</FormLabel>
                                  <FormControl>
                                    <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>

                        <FormField
                          control={form.control}
                          name="winnerStart"
                          render={({ field }) => (
                            <FormItem className="flex items-center justify-between rounded-lg border p-4">
                              <div>
                                <FormLabel>Winner Starts Next Round</FormLabel>
                                <FormDescription>Winner of previous round starts the next round</FormDescription>
                              </div>
                              <FormControl>
                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="dryMode"
                          render={({ field }) => (
                            <FormItem className="flex items-center justify-between rounded-lg border p-4">
                              <div>
                                <FormLabel>Dry Mode</FormLabel>
                                <FormDescription>Reveal 6 or 7 cards</FormDescription>
                              </div>
                              <FormControl>
                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="underTenEnabled"
                          render={({ field }) => (
                            <FormItem className="flex items-center justify-between rounded-lg border p-4">
                              <div>
                                <FormLabel>Under Ten Rule</FormLabel>
                                <FormDescription>Enable under ten rule</FormDescription>
                              </div>
                              <FormControl>
                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    {/* Game Score Settings */}
                    <div className="space-y-4">
                      <h3 className="font-medium">Game Score Settings</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="gameScore.numberOfRounds"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Number of Rounds</FormLabel>
                              <FormControl>
                                <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="gameScore.pointsToWin"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Points to Win</FormLabel>
                              <FormControl>
                                <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="gameScore.direction"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Score Direction</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="asc">Ascending</SelectItem>
                                <SelectItem value="desc">Descending</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Timer Settings */}
                    <div className="space-y-4">
                      <h3 className="font-medium">Timer Settings</h3>
                      <FormField
                        control={form.control}
                        name="timer.timerEnabled"
                        render={({ field }) => (
                          <FormItem className="flex items-center justify-between rounded-lg border p-4">
                            <div>
                              <FormLabel>Enable Timer</FormLabel>
                              <FormDescription>Enable countdown for playing cards</FormDescription>
                            </div>
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="timer.timerDuration"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Timer Duration (seconds)</FormLabel>
                              <FormControl>
                                <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="timer.timerWarning"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Warning Time (seconds)</FormLabel>
                              <FormControl>
                                <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="timer.timerWarningEnabled"
                        render={({ field }) => (
                          <FormItem className="flex items-center justify-between rounded-lg border p-4">
                            <div>
                              <FormLabel>Enable Timer Warning</FormLabel>
                              <FormDescription>Show warning when timer is low</FormDescription>
                            </div>
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Timeout Settings */}
                    <div className="space-y-4">
                      <h3 className="font-medium">Timeout Settings</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="searchTimeOut"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Search Timeout (ms)</FormLabel>
                              <FormControl>
                                <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                              </FormControl>
                              <FormDescription>Time for accepting the match</FormDescription>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="inactivityTimeoutTime"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Inactivity Timeout (ms)</FormLabel>
                              <FormControl>
                                <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                              </FormControl>
                              <FormDescription>Time before game is shut down due to inactivity</FormDescription>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <Button type="submit" className="w-full" disabled={updateSettingsMutation.isPending}>
                      {updateSettingsMutation.isPending ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save Settings"
                      )}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}